import { supportsRegexLookbehind } from '#/store/services/BrowserGuard/Logic/browserFeatureSupport'
import {
  BrowserUpdateTarget,
  MessageKeys,
  MessageVariant,
  SupportedBrowser,
  SupportedBrowserKey,
  SystemUpdateButton,
} from '#/store/services/BrowserGuard/types'

const MESSAGE_KEYS: Record<MessageVariant, MessageKeys> = {
  browser: {
    benefits: 'browser_guard_benefits',
    footer: 'browser_guard_footer',
    message: 'browser_guard_message',
    title: 'browser_guard_title',
  },
  ios: {
    footer: 'browser_guard_footer_ios',
    message: 'browser_guard_message_ios',
    title: 'browser_guard_title_ios',
  },
  mac_safari: {
    benefits: 'browser_guard_benefits_mac',
    footer: 'browser_guard_footer_mac',
    message: 'browser_guard_message_mac',
    title: 'browser_guard_title_mac',
  },
  unsupported_feature: {
    benefits: 'browser_guard_benefits',
    footer: 'browser_guard_footer_feature',
    message: 'browser_guard_message_feature',
    title: 'browser_guard_title_feature',
  },
}

const SYSTEM_UPDATE_BUTTON: Record<Exclude<BrowserUpdateTarget, 'browser'>, SystemUpdateButton> = {
  ios: {
    alt: 'iOS',
    messageKey: 'browser_guard_update_ios_tooltip',
  },
  mac_safari: {
    alt: 'Safari',
    messageKey: 'browser_guard_update_mac_tooltip',
  },
}

export function buildDetectedBrowser({
  browserName,
  browserVersion,
}: {
  browserName: string
  browserVersion: string
}): string {
  return `${browserName} ${browserVersion}`
}

export function buildMinimumBrowser({
  minimumBrowserName,
  minimumBrowserVersion,
}: {
  minimumBrowserName?: string
  minimumBrowserVersion?: string
}): string | null {
  if (!minimumBrowserName || !minimumBrowserVersion) {
    return null
  }

  return `${minimumBrowserName} ${minimumBrowserVersion}`
}

export function getMatchedBrowser({
  browserName,
  supportedBrowsers,
}: {
  browserName: string
  supportedBrowsers: readonly SupportedBrowser[]
}): SupportedBrowser | undefined {
  const normalizedBrowserName = browserName.toLowerCase()

  return supportedBrowsers.find(browser => normalizedBrowserName.includes(browser.key))
}

export function getMessageVariant({
  minimumBrowser,
  updateTarget,
}: {
  minimumBrowser: string | null
  updateTarget: BrowserUpdateTarget
}): MessageVariant {
  if (updateTarget !== 'browser') {
    return updateTarget
  }

  if (!supportsRegexLookbehind() || !minimumBrowser) {
    return 'unsupported_feature'
  }

  return 'browser'
}

export function getMessageKeys({
  messageVariant,
}: {
  messageVariant: MessageVariant
}): MessageKeys {
  return MESSAGE_KEYS[messageVariant]
}

export function getSystemUpdateButton({
  updateTarget,
}: {
  updateTarget: BrowserUpdateTarget
}): SystemUpdateButton | null {
  if (updateTarget === 'browser') {
    return null
  }

  return SYSTEM_UPDATE_BUTTON[updateTarget]
}

export function canBypassBrowserGuard({
  matchedBrowser,
  messageVariant,
}: {
  matchedBrowser?: SupportedBrowser
  messageVariant: MessageVariant
}): boolean {
  if (messageVariant !== 'browser') {
    return false
  }

  return matchedBrowser?.key !== 'safari'
}

/**
 * iOS não permite trocar de navegador porque todos usam o mesmo WebKit;
 * nos demais alvos oferecemos as opções que não são a atual nem o Safari.
 */
export function getIconButtonBrowsers({
  matchedBrowserKey,
  supportedBrowsers,
  updateTarget,
}: {
  matchedBrowserKey?: SupportedBrowserKey
  supportedBrowsers: readonly SupportedBrowser[]
  updateTarget: BrowserUpdateTarget
}): readonly SupportedBrowser[] {
  if (updateTarget === 'ios') {
    return []
  }

  return supportedBrowsers.filter(
    browser => browser.key !== 'safari' && browser.key !== matchedBrowserKey,
  )
}

export function buildSentryReason({ updateTarget }: { updateTarget: BrowserUpdateTarget }): string {
  return updateTarget === 'browser' ? 'Unsupported browser' : 'Unsupported system'
}
